/**
 * Helper for XHR communication
 * @module lib/xhr
 */

/**
 * Sends an XHR to the given `url` and returns a promise upon its completion.
 * Statuses between 200 and 300 will be considered as successes, all other as errors
 * @param {string} url
 * @param {Object} options
 * @param {string} options.method - The HTTP method
 * @param {Object} options.data - The data to send alongside the request
 * @param {Object} options.header - The headers to set on the request
 * @param {Object} options....opts - All other parameters will set on the xhr itself to configure it
 */
export function send(
  url,
  { method = 'GET', data, headers = {}, ...opts } = {}
) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open(method, url, true);
    xhr.addEventListener('load', e => {
      if (e.target.status >= 200 && e.target.status < 300) {
        resolve(e);
      } else {
        reject(e);
      }
    });
    xhr.addEventListener('error', reject);
    xhr.addEventListener('abort', reject);
    xhr.addEventListener('timeout', reject);
    Object.entries(headers).forEach(([name, value]) =>
      xhr.setRequestHeader(name, value)
    );
    Object.entries(opts).forEach(([name, value]) => {
      xhr[name] = value;
    });
    xhr.send(data);
  }).finally(e => {
    // IE and Edge don't support responseType = 'json'
    if (opts.responseType === 'json' && typeof xhr.response === 'string') {
      xhr.parsedResponse = JSON.parse(xhr.response);
    } else {
      xhr.parsedResponse = xhr.response;
    }
    return e;
  });
  // Expose the xhr to allow abortion and listening to other events
  promise.xhr = xhr;
  return promise;
}

/**
 * Wraps a method sending XHRs so that future calls cancel any pending request
 * @param {Function} send
 */
export function withPreviousRequestCancellation(send) {
  let currentRequest;
  return function(url, options) {
    if (currentRequest) {
      currentRequest.xhr.abort();
    }
    currentRequest = send(url, options);

    // Avoid uncaught exceptions
    // eslint-disable-next-line promise/catch-or-return
    currentRequest.then(
      () => (currentRequest = null),
      () => (currentRequest = null)
    );
    return currentRequest;
  };
}

/**
 * Adds the given query params to the given URL
 * @param {string} url
 * @param {Object} queryParams
 */
export function addQueryParams(url, queryParams) {
  const params = Object.entries(queryParams || {})
    .map(([key, value]) => {
      return `${key}=${value}`;
    })
    .join('&');
  const joinChar = url.indexOf('?') == -1 ? '?' : '&';
  return `${url}${joinChar}${params}`;
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/lib/xhr.js